import { message } from '@repo/ui';
import { translate, TranslationEntity } from '@repo/utils';
import { type NodeProps } from '@xyflow/react';
import NodeMenu from 'automations/components/NodeMenu';
import CreateLeadDistributionForm from 'automations/components/Nodes/CreateLeadDistributionForm';
import { useAutomationDispatch } from 'automations/hooks/useAutomationDispatch';
import useCommonNodeHandles from 'automations/hooks/useCommonNodeHandles';
import { getParentIdSelector } from 'automations/selectors/reactFlow';
import { addAdditionalOptions } from 'automations/store/reactFlowSlice';
import { type DistributeLeadNodeType } from 'automations/types/nodeSchemas/actionNodes';
import { saveActionNode } from 'automations/utils/actionNode';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';

export default function LeadDistributionContent({
  node,
}: {
  node: NodeProps<DistributeLeadNodeType>;
}) {
  const { setMenuOpen, contentMode } = useCommonNodeHandles(node);
  const dispatch = useAutomationDispatch();
  const leadDistributionId = node.data?.additionalOptions?.leadDistributionId;
  const parentId = useSelector(getParentIdSelector(node.id));

  const handleFinish = useCallback(async (id?: number) => {
    if (!id) return;
    try {
      dispatch(addAdditionalOptions({
        nodeId: node.id,
        additionalOptions: {
          leadDistributionId: id,
        },
      }));
      await dispatch(saveActionNode(node.id, parentId, contentMode));
      setMenuOpen(false);
    } catch (error) {
      message.error('Something went wrong');
    }
  }, [contentMode, dispatch, node.id, parentId, setMenuOpen]);

  const mode = contentMode === 'create' ? 'Create' : 'Edit';

  return (
    <NodeMenu
      drawerProps={{
        title: translate(
          `Action: ${mode} #{${TranslationEntity.lead}} Distribution`,
          [{ capitalize: true }],
        ),
        destroyOnClose: true,
      }}
      node={node}
    >
      <CreateLeadDistributionForm
        onFinish={handleFinish}
        leadDistributionId={leadDistributionId}
      />
    </NodeMenu>
  );
}
